import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Check, Circle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TimelineStep } from '@/lib/booking/timeline';

interface BookingTimelineProps {
  steps: TimelineStep[];
}

function StepIcon({ status }: { status: TimelineStep['status'] }) {
  if (status === 'completed') {
    return (
      <div className="flex items-center justify-center h-7 w-7 rounded-full bg-primary text-primary-foreground shrink-0">
        <Check className="h-4 w-4" />
      </div>
    );
  }
  if (status === 'current') {
    return (
      <div className="flex items-center justify-center h-7 w-7 rounded-full border-2 border-primary bg-background shrink-0">
        <Circle className="h-3 w-3 fill-primary text-primary" />
      </div>
    );
  }
  if (status === 'cancelled') {
    return (
      <div className="flex items-center justify-center h-7 w-7 rounded-full bg-muted text-muted-foreground shrink-0">
        <X className="h-4 w-4" />
      </div>
    );
  }
  // upcoming
  return (
    <div className="flex items-center justify-center h-7 w-7 rounded-full border-2 border-muted bg-background shrink-0" />
  );
}

function formatStepDate(dateStr: string): string {
  try {
    return format(new Date(dateStr), 'd MMM yyyy', { locale: sv });
  } catch {
    return '';
  }
}

export function BookingTimeline({ steps }: BookingTimelineProps) {
  return (
    <div className="relative" role="list" aria-label="Bokningens status">
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;

        return (
          <div key={step.key} className="flex gap-4" role="listitem">
            {/* Connector + icon column */}
            <div className="flex flex-col items-center">
              <StepIcon status={step.status} />
              {!isLast && (
                <div
                  className={cn(
                    'w-0.5 flex-1 min-h-[24px]',
                    step.status === 'completed' ? 'bg-primary/40' : 'bg-muted',
                  )}
                />
              )}
            </div>

            {/* Content */}
            <div className={cn('pb-6', isLast && 'pb-0')}>
              <p
                className={cn(
                  'text-sm font-medium leading-7',
                  step.status === 'completed' && 'text-foreground',
                  step.status === 'current' && 'text-foreground',
                  step.status === 'upcoming' && 'text-muted-foreground',
                  step.status === 'cancelled' && 'text-muted-foreground line-through',
                )}
              >
                {step.label}
              </p>
              {step.description && (
                <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
              )}
              {step.date && (
                <p className="text-xs text-muted-foreground mt-0.5">{formatStepDate(step.date)}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
